import type { ComponentType, ReactNode } from "react";
import classNames from "classnames";
import { ProfileChampionCard } from "./PlusTierListVisualization";
import { TIER_GRADE_STYLES, getTierGradeColorHex } from "@ugg/shared/utils/tier-helpers";
import { RoleS } from "@ugg/shared/utils/role-helpers";
import { AppRoutes } from "@ugg/shared/routes/app-routes";
import type { BasicChampionPerformanceWithTier } from "@ugitgud/legos/queries/lol";

export type PlusLinkComponent = ComponentType<{ to: string; className?: string; children: ReactNode }>;
interface PlusTierListCTAPageProps {
  className?: string;
  PlusLink: PlusLinkComponent;
  ClickHereButton: PlusLinkComponent;
  clickHereLink: string;
}

const JINX_CHAMPION_ID = 222;
const CAITLYN_CHAMPION_ID = 51;

const PREVIEW_STATS: BasicChampionPerformanceWithTier[] = [
  {
    __typename: "BasicChampionPerformanceWithTier",
    championId: JINX_CHAMPION_ID,
    wins: 8,
    totalMatches: 12,
    tier: "S+",
  },
  {
    __typename: "BasicChampionPerformanceWithTier",
    championId: CAITLYN_CHAMPION_ID,
    wins: 12,
    totalMatches: 20,
    tier: "S+",
  },
];

export function PlusTierListCTAPage({ className, PlusLink, ClickHereButton, clickHereLink }: PlusTierListCTAPageProps) {
  const tierColor = getTierGradeColorHex("S+");
  const tierStyle = TIER_GRADE_STYLES["S+"];

  return (
    <div className={classNames("flex flex-col items-center w-full bg-purple-200 rounded-[3px] pt-[48px] pb-[60px]", className)}>
      <div className="relative">
        <div
          className={classNames(
            "absolute -top-3 left-1/2 -translate-x-1/2 z-10 flex items-center justify-center px-1.5 py-1 rounded-[3px] border text-xs font-bold leading-none whitespace-nowrap",
            tierStyle.badgeClass,
          )}
        >
          S+
        </div>
        <div
          className={classNames(
            "w-[224px] rounded-[3px] border-t p-4.5 shadow-[0_2px_2px_0_rgba(0,0,0,0.4)]",
            tierStyle.cardClass,
          )}
          style={{
            backgroundImage: `linear-gradient(137deg, ${tierColor}1C 0%, ${tierColor}0B 52%, ${tierColor}00 100%), linear-gradient(90deg, #191937 0%, #191937 100%)`,
          }}
        >
          <div className="flex justify-center gap-3">
            {PREVIEW_STATS.map((stat) => (
              <ProfileChampionCard
                key={stat.championId}
                stat={stat}
                role={RoleS.BOTTOM}
                tierColor={tierColor}
                interactive={false}
              />
            ))}
          </div>
        </div>
      </div>

      <p className="mt-6 text-[16px] text-lavender-50 text-center">
        Personalized Tier Lists are currently a <span className="font-semibold text-white">U.GG PLUS</span> exclusive feature.
      </p>
      <p className="mt-3 text-[16px] text-lavender-50 text-center">
        Already a member?{" "}
        <ClickHereButton to={clickHereLink} className="text-accent-blue-400 underline hover:no-underline">
          Click here
        </ClickHereButton>{" "}
        to view your own tier list!
      </p>

      <p className="mt-[42px] text-xs text-lavender-50 text-center">Not a PLUS member?</p>
      <PlusLink
        to={AppRoutes.PLUS}
        className="group relative mt-3 flex items-center justify-center w-[264px] px-3 py-2 rounded-[6px] border border-accent-yellow-400 bg-[rgba(50,42,47,0.9)] shadow-[0_4px_2px_0_rgba(0,0,0,0.25)] text-sm text-white transition-all"
      >
        <span
          className="absolute inset-0 rounded-[6px] opacity-0 group-hover:opacity-100 transition-opacity"
          style={{
            background: "linear-gradient(0deg, rgba(244, 200, 116, 0.05) 0%, rgba(244, 200, 116, 0.05) 100%), #25254B",
          }}
        />
        <span className="relative">
          Try&nbsp;<span className="font-semibold text-accent-yellow-400">U.GG PLUS</span>&nbsp;Free for 7 days
        </span>
      </PlusLink>
    </div>
  );
}
